import { Vector3 } from 'three';
import { TrackDef, TrackNode } from './spline';

/**
 * World-up projected ⊥ tangent has length² = 1 − tan.y². Below this the
 * tangent is too vertical to define a gravity-relative bank (0° = rails level
 * with the ground).
 */
export const GRAVITY_BANK_MIN_LEN_SQ = 0.15;

/** True when gravity bank is well-defined (track is not going vertical). */
export function gravityBankDefinable(tan: Vector3): boolean {
  return 1 - tan.y * tan.y >= GRAVITY_BANK_MIN_LEN_SQ;
}

/** Heading (°) in the XZ plane and nose pitch (°) from a unit tangent. */
export function yawPitchFromTan(tan: Vector3): { yaw: number; pitch: number } {
  const horiz = Math.hypot(tan.x, tan.z);
  const pitch = (Math.atan2(tan.y, Math.max(horiz, 1e-12)) * 180) / Math.PI;
  const yaw = (Math.atan2(tan.x, tan.z) * 180) / Math.PI;
  return { yaw, pitch };
}

/** Unit tangent from yaw/pitch — inverse of `yawPitchFromTan` for pitch ∈ (−90°, 90°). */
export function tanFromYawPitch(yawDeg: number, pitchDeg: number, out = new Vector3()): Vector3 {
  const yaw = (yawDeg * Math.PI) / 180;
  const pitch = (pitchDeg * Math.PI) / 180;
  const cosP = Math.cos(pitch);
  return out.set(Math.sin(yaw) * cosP, Math.sin(pitch), Math.cos(yaw) * cosP).normalize();
}

/** Outgoing tangent at a node (falls back to −hIn, then +X). */
export function nodeTangent(n: TrackNode, out = new Vector3()): Vector3 {
  out.copy(n.hOut);
  if (out.lengthSq() < 1e-12) out.copy(n.hIn).negate();
  if (out.lengthSq() < 1e-12) out.set(1, 0, 0);
  return out.normalize();
}

export function hasForcedYaw(n: TrackNode): boolean {
  return n.forcedYawDeg !== undefined;
}

export function hasForcedPitch(n: TrackNode): boolean {
  return n.forcedPitchDeg !== undefined;
}

export function hasForcedOrientation(n: TrackNode): boolean {
  return hasForcedYaw(n) || hasForcedPitch(n);
}

/** Point handles along `yaw`/`pitch`, keeping existing handle lengths. */
export function setNodeYawPitch(n: TrackNode, yawDeg: number, pitchDeg: number): void {
  const tan = tanFromYawPitch(yawDeg, pitchDeg);
  const outLen = Math.max(n.hOut.length(), 0.5);
  const inLen = Math.max(n.hIn.length(), 0.5);
  n.hOut.copy(tan).multiplyScalar(outLen);
  n.hIn.copy(tan).multiplyScalar(-inLen);
}

/**
 * After autosmooth: for nodes with forced yaw and/or pitch, capture the auto
 * angles from the smoothed handles, then rewrite handles using each forced
 * channel (missing channels keep the auto value).
 * Returns auto yaw/pitch by node id (for UI while an override is on).
 */
export function applyForcedOrientations(track: TrackDef): Map<string, { yaw: number; pitch: number }> {
  const auto = new Map<string, { yaw: number; pitch: number }>();
  for (const n of track.nodes) {
    if (!hasForcedOrientation(n)) continue;
    const autoAngles = yawPitchFromTan(nodeTangent(n));
    auto.set(n.id, autoAngles);
    setNodeYawPitch(n, n.forcedYawDeg ?? autoAngles.yaw, n.forcedPitchDeg ?? autoAngles.pitch);
  }
  return auto;
}
